import { Check, ChevronDown, ChevronRight, Minus, Search, X } from 'lucide-react'
import * as React from 'react'
import { createPortal } from 'react-dom'

import { Input } from '@/components/ui/input'
import { cn, fuzzyIncludes } from '@/lib/utils'

const MAX_PANEL_HEIGHT = 380
const MIN_PANEL_HEIGHT = 180
const GAP = 6

export interface TreeSelectNode {
  title: string
  value: string
  children?: TreeSelectNode[]
}

interface TreeMultiSelectProps {
  /** Selected LEAF values — branch nodes only group leaves. */
  value: string[]
  onChange: (value: string[]) => void
  tree: TreeSelectNode[]
  placeholder?: string
  searchPlaceholder?: string
  loading?: boolean
  emptyText?: string
  invalid?: boolean
  disabled?: boolean
  className?: string
}

function collectLeaves(node: TreeSelectNode): TreeSelectNode[] {
  if (!node.children || node.children.length === 0) return [node]
  return node.children.flatMap(collectLeaves)
}

function TriStateBox({
  state,
  onToggle
}: {
  state: 'checked' | 'indeterminate' | 'unchecked'
  onToggle: () => void
}) {
  return (
    <span
      role="checkbox"
      aria-checked={state === 'indeterminate' ? 'mixed' : state === 'checked'}
      tabIndex={-1}
      onClick={(e) => {
        e.stopPropagation()
        onToggle()
      }}
      className={cn(
        'grid h-5 w-5 shrink-0 place-content-center rounded-md border transition-colors',
        state === 'unchecked'
          ? 'border-input bg-white'
          : 'border-primary bg-primary text-white'
      )}
    >
      {state === 'checked' && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      {state === 'indeterminate' && (
        <Minus className="h-3.5 w-3.5" strokeWidth={3} />
      )}
    </span>
  )
}

export function TreeMultiSelect({
  value,
  onChange,
  tree,
  placeholder,
  searchPlaceholder,
  loading,
  emptyText = '—',
  invalid,
  disabled,
  className
}: TreeMultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set())
  const [coords, setCoords] = React.useState<{
    top?: number
    bottom?: number
    left: number
    width: number
    maxHeight: number
  }>({ top: 0, left: 0, width: 0, maxHeight: MAX_PANEL_HEIGHT })
  const ref = React.useRef<HTMLDivElement>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)

  const leafLabels = React.useMemo(() => {
    const map = new Map<string, string>()
    const walk = (nodes: TreeSelectNode[]) => {
      for (const n of nodes) {
        if (n.children && n.children.length > 0) walk(n.children)
        else map.set(n.value, n.title)
      }
    }
    walk(tree)
    return map
  }, [tree])

  const reposition = React.useCallback(() => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const spaceBelow = window.innerHeight - r.bottom - GAP - 8
    const spaceAbove = r.top - GAP - 8
    const openUp = spaceBelow < MIN_PANEL_HEIGHT && spaceAbove > spaceBelow
    const available = openUp ? spaceAbove : spaceBelow
    const maxHeight = Math.max(
      MIN_PANEL_HEIGHT,
      Math.min(MAX_PANEL_HEIGHT, available)
    )
    const width = Math.max(r.width, 260)
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
    if (openUp) {
      setCoords({
        bottom: window.innerHeight - r.top + GAP,
        left,
        width,
        maxHeight
      })
    } else {
      setCoords({ top: r.bottom + GAP, left, width, maxHeight })
    }
  }, [])

  React.useLayoutEffect(() => {
    if (!open) return
    reposition()
    searchRef.current?.focus({ preventScroll: true })
  }, [open, reposition])

  React.useEffect(() => {
    if (!open) return
    const onScroll = () => reposition()
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        !ref.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    document.addEventListener('mousedown', onDown)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open, reposition])

  const selected = React.useMemo(() => new Set(value), [value])

  const toggleLeaf = (val: string) =>
    onChange(
      selected.has(val) ? value.filter((v) => v !== val) : [...value, val]
    )

  const toggleBranch = (node: TreeSelectNode) => {
    const leaves = collectLeaves(node).map((l) => l.value)
    const allSelected = leaves.every((l) => selected.has(l))
    if (allSelected) {
      const drop = new Set(leaves)
      onChange(value.filter((v) => !drop.has(v)))
    } else {
      onChange([...new Set([...value, ...leaves])])
    }
  }

  const toggleExpand = (val: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(val) ? next.delete(val) : next.add(val)
      return next
    })

  /**
   * A node matches when its own title matches, or any descendant matches
   * (branch stays visible, pruned to matching children), or an ancestor
   * matched (whole subtree stays visible).
   */
  const filterTree = (
    nodes: TreeSelectNode[],
    ancestorMatched: boolean
  ): TreeSelectNode[] =>
    nodes
      .map((node) => {
        const selfMatched = ancestorMatched || fuzzyIncludes(node.title, query)
        if (!node.children || node.children.length === 0) {
          return selfMatched ? node : null
        }
        const children = filterTree(node.children, selfMatched)
        if (children.length === 0 && !selfMatched) return null
        return {
          ...node,
          children: children.length > 0 ? children : node.children
        }
      })
      .filter((n): n is TreeSelectNode => n !== null)

  const searching = query.trim().length > 0
  const visibleTree = searching ? filterTree(tree, false) : tree

  const branchState = (
    node: TreeSelectNode
  ): 'checked' | 'indeterminate' | 'unchecked' => {
    const leaves = collectLeaves(node)
    let hit = 0
    for (const l of leaves) if (selected.has(l.value)) hit++
    if (hit === 0) return 'unchecked'
    return hit === leaves.length ? 'checked' : 'indeterminate'
  }

  const renderNode = (node: TreeSelectNode, depth: number): React.ReactNode => {
    const isBranch = !!node.children && node.children.length > 0
    // While searching every matched branch is force-expanded.
    const isExpanded = searching || expanded.has(node.value)

    if (!isBranch) {
      const isSelected = selected.has(node.value)
      return (
        <button
          key={node.value}
          type="button"
          onClick={() => toggleLeaf(node.value)}
          style={{ paddingLeft: 12 + depth * 22 }}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-xl border-0 bg-transparent py-1.5 pr-3 text-left text-sm transition-colors hover:bg-ink/[0.04]',
            isSelected ? 'font-semibold text-primary' : 'text-ink'
          )}
        >
          <TriStateBox
            state={isSelected ? 'checked' : 'unchecked'}
            onToggle={() => toggleLeaf(node.value)}
          />
          <span className="truncate">{node.title}</span>
        </button>
      )
    }

    return (
      <div key={node.value}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleExpand(node.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              toggleExpand(node.value)
            }
          }}
          style={{ paddingLeft: 4 + depth * 22 }}
          className="flex w-full cursor-pointer items-center gap-1.5 rounded-xl py-1.5 pr-3 text-left text-sm font-medium text-ink transition-colors hover:bg-ink/[0.04]"
        >
          <span className="grid h-5 w-5 shrink-0 place-content-center text-ink/40">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </span>
          <TriStateBox
            state={branchState(node)}
            onToggle={() => toggleBranch(node)}
          />
          <span className="truncate">{node.title}</span>
        </div>
        {isExpanded &&
          node.children!.map((child) => renderNode(child, depth + 1))}
      </div>
    )
  }

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex min-h-10 w-full items-center gap-1.5 rounded-2xl border bg-white px-3 py-1.5 text-left text-sm transition-colors hover:border-ink/30 focus:border-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-60',
          invalid ? 'border-destructive' : 'border-ink/[0.16]'
        )}
      >
        <span className="flex max-h-[100px] flex-1 flex-wrap items-center gap-1.5 overflow-y-auto">
          {value.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            value.map((val) => (
              <span
                key={val}
                className="inline-flex items-center gap-1 rounded-full bg-ink/[0.06] py-0.5 pl-2.5 pr-1 text-xs text-ink"
              >
                <span className="max-w-[14rem] truncate">
                  {leafLabels.get(val) ?? val}
                </span>
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleLeaf(val)
                  }}
                  className="flex h-4 w-4 items-center justify-center rounded-full text-ink/50 hover:text-ink"
                >
                  <X className="h-3 w-3" />
                </span>
              </span>
            ))
          )}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            data-floating-panel=""
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: coords.top,
              bottom: coords.bottom,
              left: coords.left,
              width: coords.width,
              maxHeight: coords.maxHeight
            }}
            className="pointer-events-auto z-[1300] flex flex-col overflow-hidden rounded-2xl border border-ink/[0.08] bg-white shadow-[0_16px_40px_rgba(4,33,37,0.16)]"
          >
            <div className="relative shrink-0 border-b border-ink/[0.06] p-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchRef}
                value={query}
                placeholder={searchPlaceholder ?? placeholder}
                onChange={(e) => setQuery(e.target.value)}
                className="h-9 border-0 pl-9 shadow-none focus-visible:ring-0"
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-1">
              {loading ? (
                <div className="px-3 py-3 text-sm text-muted-foreground">…</div>
              ) : visibleTree.length === 0 ? (
                <div className="px-3 py-3 text-sm text-muted-foreground">
                  {emptyText}
                </div>
              ) : (
                visibleTree.map((node) => renderNode(node, 0))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
